
import { useState, useEffect, useRef } from 'react';
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Play, Pause, RefreshCw, Volume2, VolumeX } from "lucide-react";
import { SOUND_OPTIONS } from '@/components/SoundSelector';

interface TimerProps {
  duration: number;
  onComplete: () => void;
  isActive: boolean;
  isPaused?: boolean;
  onPlayPause?: () => void;
  onReset?: () => void;
}

export const Timer = ({ 
  duration, 
  onComplete, 
  isActive, 
  isPaused = false,
  onPlayPause,
  onReset
}: TimerProps) => {
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [progress, setProgress] = useState(100);
  const { toast } = useToast();
  const [soundEnabled, setSoundEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const completionTriggeredRef = useRef(false);
  const timerCompletedRef = useRef(false);

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Reset timer when duration changes
  useEffect(() => {
    setTimeLeft(duration * 60);
    setProgress(100);
    completionTriggeredRef.current = false;
    timerCompletedRef.current = false;
  }, [duration]);

  // Set up the audio elements once on component mount
  useEffect(() => {
    // Create meditation sound audio
    const audio = new Audio(SOUND_OPTIONS[0].url);
    audio.loop = true;
    audioRef.current = audio;
    
    // Cleanup on unmount
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, []);

  // Handle sound playback based on timer state
  useEffect(() => {
    if (!audioRef.current) return;
    
    if (isActive && !isPaused && soundEnabled && !isPlayingRef.current) {
      try {
        audioRef.current.play()
          .then(() => {
            isPlayingRef.current = true;
            console.log("Meditation sound playing");
          })
          .catch(err => {
            console.error("Error playing meditation sound:", err);
            isPlayingRef.current = false;
          });
      } catch (err) {
        console.error("Error attempting to play meditation sound:", err);
      }
    } else if (isPaused || !isActive || !soundEnabled) {
      if (audioRef.current) {
        audioRef.current.pause();
        isPlayingRef.current = false;
      }
    }
  }, [isActive, isPaused, soundEnabled]);

  // Timer countdown logic
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (isActive && !isPaused && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            clearInterval(interval);
            timerCompletedRef.current = true;
            
            // Handle completion
            if (!completionTriggeredRef.current) {
              completionTriggeredRef.current = true;
              setTimeout(() => handleTimerCompletion(), 100);
            }
            return 0;
          }
          return time - 1;
        });

        setProgress((prev) => {
          const newProgress = (timeLeft - 1) / (duration * 60) * 100;
          return Math.max(0, newProgress);
        });
      }, 1000);
    } else if (timerCompletedRef.current && !completionTriggeredRef.current) {
      // Safety check for edge case where timer is at 0 but completion hasn't been triggered
      completionTriggeredRef.current = true;
      setTimeout(() => handleTimerCompletion(), 100);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, isPaused, timeLeft, duration]);

  // Separate function to handle timer completion with multiple safeguards
  const handleTimerCompletion = () => {
    console.log("Timer completed, handling completion actions");
    
    // Play sound
    try {
      const completionSound = new Audio('/notification-sound.mp3');
      completionSound.play()
        .catch(err => console.error("Error playing completion sound:", err));
    } catch (err) {
      console.error("Error creating audio:", err);
    }
    
    // Send notification
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Timer Complete!', {
          body: 'Your timer has finished.',
          icon: '/favicon.ico'
        });
      }
    } catch (err) {
      console.error("Error sending notification:", err);
    }

    // Show toast
    toast({
      title: "Timer Complete!",
      description: "Your timer has finished.",
      duration: 4000,
    });

    // Stop meditation sound if playing
    if (audioRef.current && isPlayingRef.current) {
      audioRef.current.pause();
      isPlayingRef.current = false;
    }

    // Complete the task with retry logic
    try {
      console.log("Calling onComplete callback...");
      onCompleteRef.current();
    } catch (error) {
      console.error("Error in onComplete callback:", error);
      
      // Retry once after a delay
      setTimeout(() => {
        try {
          console.log("Retrying onComplete callback...");
          onCompleteRef.current();
        } catch (retryError) {
          console.error("Final attempt to call onComplete failed:", retryError);
        }
      }, 500);
    }
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    
    // Force immediate effect
    if (newSoundEnabled && isActive && !isPaused && audioRef.current) {
      audioRef.current.play()
        .then(() => {
          isPlayingRef.current = true;
          console.log("Meditation sound enabled and playing");
        })
        .catch(err => {
          console.error("Error enabling meditation sound:", err);
          isPlayingRef.current = false;
        });
    } else if (!newSoundEnabled && audioRef.current) {
      audioRef.current.pause();
      isPlayingRef.current = false;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-2 p-3 glass-panel rounded-lg min-w-[180px] shadow-sm">
      <div className="text-xl font-bold text-primary timer-animation tracking-wider font-mono">
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </div>
      <Progress 
        value={progress} 
        className="w-full max-w-[160px] h-1"
      />
      
      {/* Sound controls - show meditation toggle when timer is active */}
      {isActive && (
        <div className="flex items-center gap-2 mt-1">
          <Button
            variant="outline"
            size="icon"
            className={`w-7 h-7 p-0 hover:bg-primary/10 hover:text-primary transition-colors ${soundEnabled ? 'text-primary' : ''}`}
            onClick={toggleSound}
            title={soundEnabled ? "Mute meditation" : "Play meditation sound"}
          >
            {soundEnabled ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
          </Button>
        </div>
      )}
      
      {/* Play/pause and reset buttons */}
      {onPlayPause && onReset && (
        <div className="flex items-center gap-2 mt-1">
          <Button
            variant="outline"
            size="icon"
            className="w-7 h-7 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
            onClick={onPlayPause}
          >
            {isPaused || !isActive ? (
              <Play className="h-3 w-3" />
            ) : (
              <Pause className="h-3 w-3" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="w-7 h-7 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
            onClick={onReset}
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};
